import { toBlob } from 'html-to-image';

export interface ShareResult {
  success: boolean;
  error?: string;
  method: 'native' | 'fallback' | 'none';
}

/**
 * Captures a DOM element and shares it along with text and URL using the Web Share API.
 * If Web Share API doesn't support file sharing or is unavailable, it falls back to 
 * sharing the URL/Text and offers the image as a direct download.
 */
export const shareElementAsImage = async (
  element: HTMLElement,
  options: {
    title: string;
    text: string;
    url: string;
    fileName?: string;
  }
): Promise<ShareResult> => {
  try {
    // Ensure fonts are ready for pixel-perfect rendering
    if (document.fonts) {
      await document.fonts.ready;
    }

    // Temporarily ensure component is not absolutely hidden just in case, though layout preserves it
    // Wait a tick for any layout shifts
    // Wait a tick for any layout shifts and image loading
    await new Promise(r => setTimeout(r, 400));

    const blob = await toBlob(element, {
      quality: 0.95,
      cacheBust: true,
      pixelRatio: 2, // Higher resolution for crisp sharing
      backgroundColor: '#ffffff', // Default fallback, but component defines its own
      fontEmbedCSS: '' // Suppress scraping of cross-origin stylesheets to eliminate benign console errors
    });

    if (!blob) {
      throw new Error('Failed to capture image blob');
    }

    const fileName = options.fileName || 'nammamap-share.png';
    // Use explicit File properties to aid parsing on some OS clients
    const file = new File([blob], fileName, { 
      type: 'image/png',
      lastModified: Date.now() 
    });

    // Consolidate text + URL to ensure all clients show both, especially when sharing files
    const consolidatedText = `${options.text}\n\nLink: ${options.url}`;

    // Check if Web Share API is supported and can share files
    if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        await navigator.share({
          files: [file],
          title: options.title,
          text: consolidatedText
          // Note: We omit 'url' parameter here because it is already appended to 'consolidatedText'.
          // Passing both triggers many OS browsers to automatically append it twice!
        });
        return { success: true, method: 'native' };
      } catch (shareError: any) {
        // Handle user cancellation without considering it a hard failure
        if (shareError.name === 'AbortError') {
          return { success: false, error: 'User cancelled sharing', method: 'native' };
        }
        // Proceed to fallback if sharing failed for other reasons
        console.warn('Native share failed, attempting fallback:', shareError);
      }
    }

    // Fallback Path:
    // 1. Offer the Image for download
    const downloadUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Revoke after slight delay
    setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);

    // 2. Copy URL to Clipboard if available
    if (options.url) {
      try {
        await navigator.clipboard.writeText(`${options.text} ${options.url}`);
      } catch (clipError) {
        console.warn('Clipboard fallback failed:', clipError);
      }
    }

    // Inform user via result
    return { 
      success: true, 
      method: 'fallback', 
      error: 'System share unavailable. Image downloaded and link copied to clipboard.' 
    };

  } catch (err: any) {
    console.error('Failed to generate share image:', err);
    return { 
      success: false, 
      error: err.message || 'Unexpected error', 
      method: 'none' 
    };
  }
};
